/**
 * Reusable notice popup (image + copy + optional CTA).
 *
 * Usage:
 *   NoticePopup.show();
 *   NoticePopup.show({
 *     eyebrow, titleHtml, bodyHtml, image, imageAlt,
 *     cta: { label, href, external }, // omit / null / false to hide
 *     dismissLabel: 'Maybe later',    // false to hide secondary link
 *     onCtaClick, onDismiss
 *   });
 *   NoticePopup.hide();
 *
 * Presentation only. CTA clicks fire onCtaClick and "notice-popup:cta-click".
 * Default navigation happens only when cta.href is set and onCtaClick does not
 * return false.
 */
(function () {
  var DEFAULT_CONFIG = {
    eyebrow: null,
    eyebrowLogo: true,
    titleHtml:
      'You’ve got your meal prep sorted.<br>But what about the kids?',
    bodyHtml:
      '<p>Tasty, nutritious meal prep for juniors aged 4-11.</p>' +
      '<p>Designed and taste tested by JNRs, with a little help from our nutritionists.</p>' +
      '<p>Made to fuel big days, busy lives and growing appetites. Ready in 3 minutes.</p>',
    image: null,
    imageAlt: 'FuelHub Junior meals',
    cta: {
      label: 'View JNR menu',
      href: '/pages/menu'
    },
    dismissLabel: 'Maybe later'
  };

  function NoticePopup() {
    this.root = null;
    this.onCtaClick = null;
    this.onDismiss = null;
    this.ctaConfig = null;
    this.defaultImageSrc = '';
    this.defaultEyebrowLogoSrc = '';
    this.lastFocused = null;
  }

  NoticePopup.prototype.init = function () {
    if (this.root) return true;

    this.root = document.getElementById('notice-popup');
    if (!this.root) return false;

    this.imageEl = this.root.querySelector('[data-notice-popup-image]');
    this.eyebrowEl = this.root.querySelector('[data-notice-popup-eyebrow]');
    this.eyebrowLogoEl = this.root.querySelector('[data-notice-popup-eyebrow-logo]');
    this.titleEl = this.root.querySelector('[data-notice-popup-title]');
    this.bodyEl = this.root.querySelector('[data-notice-popup-body]');
    this.ctaEl = this.root.querySelector('[data-notice-popup-cta]');
    this.dismissLinkEl = this.root.querySelector('[data-notice-popup-dismiss-link]');

    if (this.imageEl) {
      this.defaultImageSrc = this.imageEl.getAttribute('src') || '';
    }

    if (this.eyebrowLogoEl) {
      this.defaultEyebrowLogoSrc = this.eyebrowLogoEl.getAttribute('src') || '';
    }

    var self = this;

    this.root.addEventListener('click', function (event) {
      if (event.target.closest('[data-notice-popup-dismiss]')) {
        self.hide('close');
        return;
      }

      if (event.target.closest('[data-notice-popup-dismiss-link]')) {
        self.hide('dismiss-link');
        return;
      }

      var cta = event.target.closest('[data-notice-popup-cta]');
      if (cta && !cta.hasAttribute('hidden')) {
        self.handleCtaClick(event);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.isOpen()) self.hide('escape');
    });

    return true;
  };

  NoticePopup.prototype.isOpen = function () {
    return !!this.root && this.root.classList.contains('is-open');
  };

  NoticePopup.prototype.setText = function (el, value) {
    if (!el) return;
    var text = value == null ? '' : String(value);
    el.textContent = text;
    el.hidden = !text;
  };

  NoticePopup.prototype.renderEyebrow = function (config) {
    if (!this.eyebrowEl) return;

    var eyebrowText =
      config.eyebrow != null && config.eyebrow !== false ? String(config.eyebrow).trim() : '';
    var useLogo =
      !eyebrowText &&
      (config.eyebrowLogo === true ||
        (typeof config.eyebrowLogo === 'string' && config.eyebrowLogo) ||
        config.eyebrowLogo === undefined);

    // Clear previous text nodes but keep the logo <img>.
    Array.prototype.slice.call(this.eyebrowEl.childNodes).forEach(function (node) {
      if (node.nodeType === 3 || (node.nodeType === 1 && !node.hasAttribute('data-notice-popup-eyebrow-logo'))) {
        this.eyebrowEl.removeChild(node);
      }
    }, this);

    if (useLogo && this.eyebrowLogoEl) {
      var logoSrc =
        typeof config.eyebrowLogo === 'string' && config.eyebrowLogo
          ? config.eyebrowLogo
          : this.defaultEyebrowLogoSrc;
      if (logoSrc) this.eyebrowLogoEl.src = logoSrc;
      this.eyebrowLogoEl.hidden = !logoSrc;
      this.eyebrowEl.hidden = !logoSrc;
      this.eyebrowEl.classList.add('notice-popup__eyebrow--logo');
      this.eyebrowEl.classList.remove('notice-popup__eyebrow--text');
      return;
    }

    if (this.eyebrowLogoEl) this.eyebrowLogoEl.hidden = true;
    this.eyebrowEl.classList.remove('notice-popup__eyebrow--logo');
    this.eyebrowEl.classList.add('notice-popup__eyebrow--text');

    if (eyebrowText) {
      this.eyebrowEl.appendChild(document.createTextNode(eyebrowText));
      this.eyebrowEl.hidden = false;
    } else {
      this.eyebrowEl.hidden = true;
    }
  };

  NoticePopup.prototype.render = function (config) {
    this.renderEyebrow(config);

    if (this.titleEl) {
      this.titleEl.innerHTML = config.titleHtml || '';
    }

    if (this.bodyEl) {
      var bodyHtml = config.bodyHtml || '';
      this.bodyEl.innerHTML = bodyHtml;
      this.bodyEl.hidden = !bodyHtml;
    }

    if (this.imageEl) {
      this.imageEl.src = config.image || this.defaultImageSrc;
      this.imageEl.alt = config.imageAlt || '';
    }

    this.ctaConfig = config.cta && typeof config.cta === 'object' ? config.cta : null;

    if (this.ctaEl) {
      if (this.ctaConfig && this.ctaConfig.label) {
        this.ctaEl.hidden = false;
        this.ctaEl.textContent = this.ctaConfig.label;
        this.ctaEl.href = this.ctaConfig.href || '#';

        if (this.ctaConfig.external) {
          this.ctaEl.target = '_blank';
          this.ctaEl.rel = 'noopener noreferrer';
        } else {
          this.ctaEl.removeAttribute('target');
          this.ctaEl.removeAttribute('rel');
        }
      } else {
        this.ctaEl.hidden = true;
        this.ctaEl.removeAttribute('href');
        this.ctaEl.textContent = '';
      }
    }

    if (this.dismissLinkEl) {
      if (config.dismissLabel === false || config.dismissLabel === null) {
        this.dismissLinkEl.hidden = true;
        this.dismissLinkEl.textContent = '';
      } else {
        this.dismissLinkEl.hidden = false;
        this.dismissLinkEl.textContent =
          config.dismissLabel === undefined
            ? DEFAULT_CONFIG.dismissLabel
            : String(config.dismissLabel);
      }
    }
  };

  NoticePopup.prototype.show = function (options) {
    if (!this.init()) return;

    var config = options || {};

    this.onCtaClick = typeof config.onCtaClick === 'function' ? config.onCtaClick : null;
    this.onDismiss = typeof config.onDismiss === 'function' ? config.onDismiss : null;

    this.render({
      eyebrow: config.eyebrow !== undefined ? config.eyebrow : DEFAULT_CONFIG.eyebrow,
      eyebrowLogo:
        config.eyebrowLogo !== undefined ? config.eyebrowLogo : DEFAULT_CONFIG.eyebrowLogo,
      titleHtml: config.titleHtml !== undefined ? config.titleHtml : DEFAULT_CONFIG.titleHtml,
      bodyHtml:
        config.bodyHtml !== undefined
          ? config.bodyHtml
          : config.body !== undefined
            ? config.body
            : DEFAULT_CONFIG.bodyHtml,
      image: config.image !== undefined ? config.image : DEFAULT_CONFIG.image,
      imageAlt: config.imageAlt !== undefined ? config.imageAlt : DEFAULT_CONFIG.imageAlt,
      cta: config.cta !== undefined ? config.cta : DEFAULT_CONFIG.cta,
      dismissLabel:
        config.dismissLabel !== undefined ? config.dismissLabel : DEFAULT_CONFIG.dismissLabel
    });

    this.lastFocused = document.activeElement;
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('notice-popup-open');

    var closeButton = this.root.querySelector('.notice-popup__close');
    if (closeButton) closeButton.focus();

    document.dispatchEvent(new CustomEvent('notice-popup:show'));
  };

  NoticePopup.prototype.hide = function (reason) {
    if (!this.root || !this.isOpen()) return;

    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('notice-popup-open');

    if (this.onDismiss) this.onDismiss(reason || 'close');
    document.dispatchEvent(
      new CustomEvent('notice-popup:hide', { detail: { reason: reason || 'close' } })
    );

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  NoticePopup.prototype.handleCtaClick = function (event) {
    var detail = { cta: this.ctaConfig };

    var allowDefault = true;
    if (this.onCtaClick) {
      allowDefault = this.onCtaClick(this.ctaConfig, detail) !== false;
    }

    document.dispatchEvent(new CustomEvent('notice-popup:cta-click', { detail: detail }));

    var href = this.ctaConfig && this.ctaConfig.href;
    if (!href || href === '#') {
      event.preventDefault();
      this.hide('cta');
      return;
    }

    if (!allowDefault) {
      event.preventDefault();
      this.hide('cta');
      return;
    }

    this.hide('cta');
  };

  window.NoticePopup = new NoticePopup();

  var JNR_NOTICE_STORAGE_KEYS = {
    home: 'fuelhub_jnr_notice_seen_at_home',
    subscriptions: 'fuelhub_jnr_notice_seen_at_subscriptions'
  };
  var JNR_NOTICE_COOLDOWN_MS = 4 * 24 * 60 * 60 * 1000; // 4 days

  function getJuniorNoticeAutoShowPage() {
    var path = (window.location.pathname || '').replace(/\/+$/, '') || '/';

    if (path === '/') return 'home';

    if (
      path === '/account' &&
      new URLSearchParams(window.location.search).get('view') === 'subscriptions'
    ) {
      return 'subscriptions';
    }

    return null;
  }

  function getJuniorNoticeSeenAt(storageKey) {
    try {
      var raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      var value = parseInt(raw, 10);
      return isNaN(value) ? null : value;
    } catch (error) {
      return null;
    }
  }

  function markJuniorNoticeSeen(storageKey) {
    try {
      window.localStorage.setItem(storageKey, String(Date.now()));
    } catch (error) {
      // Ignore storage failures (private mode, blocked storage, etc.)
    }
  }

  function shouldShowJuniorNoticeAuto(storageKey) {
    var seenAt = getJuniorNoticeSeenAt(storageKey);
    if (seenAt == null) return true;
    return Date.now() - seenAt >= JNR_NOTICE_COOLDOWN_MS;
  }

  // Preview: ?notice_popup=1 (or =no-cta) forces the UI on any page.
  // Auto: homepage + subscriptions, logged-in only, each with their own 4-day timer.
  document.addEventListener('DOMContentLoaded', function () {
    var params = new URLSearchParams(window.location.search);

    if (params.has('notice_popup')) {
      var hideCta = params.get('notice_popup') === 'no-cta';

      window.NoticePopup.show({
        cta: hideCta ? null : undefined,
        dismissLabel: hideCta ? false : undefined,
        onCtaClick: function (cta) {
          console.log('[NoticePopup] CTA clicked:', cta && cta.label);
        }
      });
      return;
    }

    var page = getJuniorNoticeAutoShowPage();
    if (!page) return;

    // Homepage / subscriptions auto-show: logged-in customers only.
    var isLoggedIn =
      document.body.classList.contains('logged-in') ||
      (typeof challengeData !== 'undefined' && challengeData && challengeData.id);
    if (!isLoggedIn) return;

    var storageKey = JNR_NOTICE_STORAGE_KEYS[page];
    if (!storageKey || !shouldShowJuniorNoticeAuto(storageKey)) return;

    window.NoticePopup.show({
      onDismiss: function (reason) {
        if (
          reason === 'close' ||
          reason === 'dismiss-link' ||
          reason === 'escape' ||
          reason === 'cta'
        ) {
          markJuniorNoticeSeen(storageKey);
        }
      }
    });
  });
})();
